import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch } from 'react-redux';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { cloneDeep } from 'es-toolkit';

import { Box, Paragraph, Toast, useToast } from '@zillow/constellation-10';

import { PhotoDetailsModal } from '@zg-rentals/maintenance-utils';
import { useQueryClient } from '@zg-rentals/react-query5';

import PropertiesActions from '../../actions/PropertiesActions';
import { useMultiplexBuildingSessionId, useMultiplexListingFlow } from '../../hooks/queries/multiplex';
import useMultiplexPhotoCaption from '../../hooks/queries/multiplex/useMultiplexPhotoCaption';
import useMultiplexPhotoDelete from '../../hooks/queries/multiplex/useMultiplexPhotoDelete';
import useMultiplexPhotoRank from '../../hooks/queries/multiplex/useMultiplexPhotoRank';
import {
  applyLocalStateAfterPhotoDelete,
  useDeletePhoto,
  useSortPhoto,
  useUpdatePhotoCaption,
} from '../../hooks/queries/photos';
import useIsOnReviewStep from '../../hooks/useIsOnReviewStep';
import { useSelector } from '../../selectors/useSelector';
import { getPhotoUrls } from '../../utils/propertiesPageUtils';

import { GET_LISTING_RECS } from '../../constants/RecommendationsConstants';

import PhotoUploadDropzone from '../../components/PhotoUpload';
import DiscriminatoryContentErrorAlert from './components/DiscriminatoryContentErrorAlert';
import SortableItem from './components/SortableItem';

import { StyledOrderedList } from './styles';

import type { DragEndEvent } from '@dnd-kit/core';
import type { Dispatch, SetStateAction } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';

type Props = { uploadingCount: number; setUploadingCount: Dispatch<SetStateAction<number>> };

const DEFAULT_CAPTION_SAVE_ERROR_MESSAGE = 'There was an error saving your caption.';

type LegacyCaptionSaveContext = {
  photos: Array<PhotoProps>;
  photoId: string;
  caption: string;
  discriminatoryContentErrors: Array<{ photoId: string; caption: string }>;
  setDiscriminatoryContentErrors: Dispatch<SetStateAction<Array<{ photoId: string; caption: string }>>>;
  commitPhotos: (nextPhotos: Array<PhotoProps>) => void;
};

const applyCaptionSaveError = (context: LegacyCaptionSaveContext, errorMessage?: string): void => {
  const { photos, photoId, caption, commitPhotos, discriminatoryContentErrors, setDiscriminatoryContentErrors } =
    context;
  const displayError = errorMessage || DEFAULT_CAPTION_SAVE_ERROR_MESSAGE;
  const copyPhotos = cloneDeep(photos);
  const indexToUpdate = copyPhotos.findIndex((photo) => photo.id === photoId);

  if (indexToUpdate === -1) {
    return;
  }

  copyPhotos[indexToUpdate].error = displayError;
  copyPhotos[indexToUpdate].caption = caption;
  commitPhotos(copyPhotos);

  if (errorMessage?.includes('Good Neighbor Policy')) {
    setDiscriminatoryContentErrors([...discriminatoryContentErrors, { photoId, caption }]);
  }
};

const applyCaptionSaveSuccess = (
  context: Pick<LegacyCaptionSaveContext, 'photos' | 'photoId' | 'caption' | 'commitPhotos'>,
): void => {
  const { photos, photoId, caption, commitPhotos } = context;
  const copyPhotos = cloneDeep(photos);
  const indexToUpdate = copyPhotos.findIndex((photo) => photo.id === photoId);

  if (indexToUpdate === -1) {
    return;
  }

  copyPhotos[indexToUpdate].caption = caption;
  copyPhotos[indexToUpdate].error = undefined;
  commitPhotos(copyPhotos);
};

const PhotoGallery = ({ uploadingCount, setUploadingCount }: Props) => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const [photos, setPhotos] = useState<Array<PhotoProps>>([]);
  const [clickedPhotoIdx, setClickedPhotoIdx] = useState<number | undefined | null>();
  const [discriminatoryContentErrors, setDiscriminatoryContentErrors] = useState<
    Array<{ photoId: string; caption: string }>
  >([]);

  const {
    app: { isRunningInProd },
    env: { zgApiPublic },
    user: {
      features: { canUpdateListing },
    },
    properties: {
      currentProperty,
      currentProperty: {
        isMultifamily,
        isFeed,
        isArchived,
        listingAlias,
        restrictions: { isUpdateListingRestricted },
        selfServeOnboardingStatus: { isSelfServeOnboardingInProgress, isUpdateRestricted: isSSOBUpdateRestricted },
      },
    },
  } = useSelector((state) => state);

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const isOnReviewStep = useIsOnReviewStep();

  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const { mutateAsync: multiplexDeletePhotoAsync } = useMultiplexPhotoDelete({
    meta: {
      invalidates: ['getMultiplexUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoDelete',
        details: 'PhotoGallery',
      },
    },
  });

  const { mutateAsync: multiplexSortPhotoAsync } = useMultiplexPhotoRank({
    meta: {
      invalidates: ['getMultiplexUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoRank',
        details: 'PhotoGallery',
      },
    },
  });

  const { mutateAsync: multiplexPhotoCaptionAsync } = useMultiplexPhotoCaption({
    meta: {
      invalidates: ['getMultiplexUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoCaption',
        details: 'PhotoGallery',
      },
    },
  });

  const { mutateAsync: sortPhotosAsync } = useSortPhoto(listingAlias);
  const { mutateAsync: deletePhoto } = useDeletePhoto(discriminatoryContentErrors, setDiscriminatoryContentErrors);
  const { mutateAsync: updatePhotoCaption } = useUpdatePhotoCaption();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // there isn't a way to disable so basically the hack is to not engage the drag and drop till the photo is moved greater than 3000px which is not common on desktop
        distance: isFeed ? 3000 : 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: isFeed ? 3000 : 5,
      },
    }),
  );

  useEffect(() => {
    if (currentProperty?.photos) {
      setPhotos(currentProperty.photos);
    }
  }, [currentProperty?.photos]);

  const handleDragEnd = async (event: DragEndEvent) => {
    if (uploadingCount) {
      return;
    }

    const { active, over } = event;

    if (active.id === over?.id || !over) {
      return;
    }

    let nextPhotos: Array<PhotoProps> | undefined;

    // Apply reorder synchronously so `nextPhotos` is set before the rank API runs (avoids deferred
    // `setState` batching when `onDragEnd` is not a React DOM event).
    flushSync(() => {
      setPhotos((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        if (oldIndex === -1 || newIndex === -1) {
          return items;
        }

        const sortedArray: Array<PhotoProps> = arrayMove(items, oldIndex, newIndex);
        const sortedArraysWithUpdatedRank = sortedArray.map((photo, index) => ({ ...photo, rank: index + 1 }));

        nextPhotos = sortedArraysWithUpdatedRank;
        dispatch(PropertiesActions.updateCurrentProperty({ photos: sortedArraysWithUpdatedRank }));
        return sortedArraysWithUpdatedRank;
      });
    });

    if (!nextPhotos) {
      return;
    }

    const invalidateAfterSort = () => {
      queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
      queryClient.invalidateQueries({ queryKey: ['getMultiplexUnits'] });
      queryClient.invalidateQueries({ queryKey: ['getGroupableProperties'] });
    };

    const ranksPayload = nextPhotos.map((p) => ({ id: p.id, rank: String(p.rank) }));
    const postData = { ranks: ranksPayload };
    const mutationFunc =
      isOnReviewStep === false && isInMultiplexListingFlow ? multiplexSortPhotoAsync : sortPhotosAsync;

    const reorderPromise =
      isOnReviewStep === false && isInMultiplexListingFlow
        ? (mutationFunc as typeof multiplexSortPhotoAsync)({
            getParams: { multiplexBuildingSessionId: multiplexBuildingSessionId as string },
            postData,
          })
        : (mutationFunc as typeof sortPhotosAsync)(nextPhotos);

    try {
      await reorderPromise;
      invalidateAfterSort();
    } catch {
      enqueueToast(
        <Toast tone="critical">Error occurred while reordering photos, refresh the page to try again</Toast>,
      );
    }
  };

  const handleDeletePhoto = async ({
    photoIdAtSlidePosition,
    onComplete,
  }: {
    photoIdAtSlidePosition: string;
    onComplete: () => void;
  }) => {
    try {
      if (isOnReviewStep === false && isInMultiplexListingFlow) {
        await multiplexDeletePhotoAsync({
          getParams: {
            multiplexBuildingSessionId: multiplexBuildingSessionId as string,
            photoId: photoIdAtSlidePosition,
          },
        });

        /**
         * Non-multiplex deletes run through `useDeletePhoto`, which calls `applyLocalStateAfterPhotoDelete` after the
         * single-listing API succeeds. Multiplex uses `useMultiplexPhotoDelete` instead, so we invoke the same helper
         * here to update Redux `currentProperty.photos` and discriminatory-caption state in one place.
         */
        applyLocalStateAfterPhotoDelete(
          photos,
          discriminatoryContentErrors,
          photoIdAtSlidePosition,
          setDiscriminatoryContentErrors,
          dispatch,
        );
      } else {
        await deletePhoto(photoIdAtSlidePosition);
      }

      setPhotos((prev) => {
        const indexOfDeletedPhoto = prev.findIndex((photo: PhotoProps) => photo.id === photoIdAtSlidePosition);
        if (indexOfDeletedPhoto === -1) {
          return prev;
        }
        const photosCopy = cloneDeep(prev);
        photosCopy.splice(indexOfDeletedPhoto, 1);
        return photosCopy;
      });

      if (isInMultiplexListingFlow) {
        setClickedPhotoIdx(null);
      }

      queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
      queryClient.invalidateQueries({ queryKey: ['getMultiplexUnits'] });
      queryClient.invalidateQueries({ queryKey: ['getGroupableProperties'] });

      if (!isMultifamily) {
        enqueueToast(<Toast tone="success">Photo deleted</Toast>);
      }
    } catch {
      enqueueToast(<Toast tone="critical">There was an error with deleting the photo</Toast>);
    } finally {
      setTimeout(() => {
        onComplete();
      }, 0);
    }
  };

  const handleCaptionUpdate = async ({
    photoId,
    caption,
    onComplete,
  }: {
    photoId: string;
    caption: string;
    onComplete: () => void;
  }) => {
    const commitPhotos = (nextPhotos: Array<PhotoProps>) => {
      dispatch(PropertiesActions.updateCurrentProperty({ photos: nextPhotos }));
    };

    const captionSaveContext: LegacyCaptionSaveContext = {
      photos,
      photoId,
      caption,
      discriminatoryContentErrors,
      setDiscriminatoryContentErrors,
      commitPhotos,
    };

    try {
      if (isOnReviewStep === false && isInMultiplexListingFlow) {
        const result = await multiplexPhotoCaptionAsync({
          getParams: {
            multiplexBuildingSessionId: multiplexBuildingSessionId as string,
            photoId,
            caption,
          },
        });

        if (!result.success) {
          applyCaptionSaveError(captionSaveContext, result.errorMessage);
        } else {
          applyCaptionSaveSuccess(captionSaveContext);
        }
      } else {
        await updatePhotoCaption({ photoId, caption });
      }
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'error' in err
          ? (err as { error?: { message?: string } }).error?.message
          : undefined;
      if (message) {
        applyCaptionSaveError(captionSaveContext, message);
      }
    } finally {
      setTimeout(() => {
        onComplete();
      }, 0);
    }
  };

  const handleClearError = ({ photoAtSlidePosition }: { photoAtSlidePosition: PhotoProps }) => {
    if (photoAtSlidePosition.error) {
      const copyPhotos = cloneDeep(photos);
      const indexToUpdate = copyPhotos.findIndex((copyPhoto) => copyPhoto.id === photoAtSlidePosition.id);

      copyPhotos[indexToUpdate].error = undefined;

      dispatch(PropertiesActions.updateCurrentProperty({ photos: copyPhotos }));

      const copyDiscriminatoryContent = cloneDeep(discriminatoryContentErrors);
      const errorIndexToUpdate = copyDiscriminatoryContent.findIndex(
        (error) => error.photoId === photoAtSlidePosition.id,
      );
      copyDiscriminatoryContent.splice(errorIndexToUpdate, 1);

      setDiscriminatoryContentErrors(copyDiscriminatoryContent);
    }
  };

  if (!photos) {
    return null;
  }

  return (
    <div data-testid="PhotoGallery">
      {!isFeed && <Paragraph css={{ marginBottom: '400' }}>Drag and drop to reorder</Paragraph>}

      {discriminatoryContentErrors.length > 0 && (
        <DiscriminatoryContentErrorAlert errors={discriminatoryContentErrors} />
      )}

      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          columnGap: '400',
          rowGap: '400',
        }}
      >
        {!isFeed && (
          <Box css={{ gridColumnStart: '1', gridColumnEnd: 'span 1' }}>
            <PhotoUploadDropzone
              isSmallDropzone={true}
              setUploadingCount={setUploadingCount}
              uploadingCount={uploadingCount}
            />
          </Box>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={photos} strategy={rectSortingStrategy}>
            <StyledOrderedList aria-label="property photos">
              {(photos as Array<PhotoProps>).map((photo: PhotoProps, idx: number) => (
                <SortableItem
                  key={photo.id}
                  errors={discriminatoryContentErrors}
                  handlePhotoClick={() => setClickedPhotoIdx(idx)}
                  photo={photo}
                  index={idx}
                  isDisabled={!!uploadingCount}
                  isRunningInProd={isRunningInProd}
                />
              ))}
            </StyledOrderedList>

            {uploadingCount > 0 &&
              Array.from(Array(uploadingCount)).map((_, idx) => (
                <SortableItem
                  key={`placeholder-photo-${idx}`}
                  isUploading={uploadingCount > 0}
                  photo={{ id: '' }}
                  index={idx}
                  isDisabled={true}
                  isRunningInProd={isRunningInProd}
                />
              ))}
          </SortableContext>
        </DndContext>
      </Box>

      {!!(clickedPhotoIdx || clickedPhotoIdx === 0) && (
        <PhotoDetailsModal
          additionalDisabledChecks={
            Boolean(uploadingCount > 0) ||
            isFeed ||
            !canUpdateListing ||
            isArchived ||
            isUpdateListingRestricted ||
            (isSelfServeOnboardingInProgress && isSSOBUpdateRestricted)
          }
          idx={clickedPhotoIdx}
          onCaptionUpdate={handleCaptionUpdate}
          onClearError={handleClearError}
          onClose={() => setClickedPhotoIdx(null)}
          onDeletePhoto={handleDeletePhoto}
          photos={photos}
          getPhotoUrls={getPhotoUrls}
          isRunningInProd={isRunningInProd}
        />
      )}
    </div>
  );
};

export default PhotoGallery;
